import React, { useEffect, useState } from "react";
import { BookOpen, Plus, Clock, Landmark, Sparkles, Search, Send, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, StatCard, Badge, Button, Input, Field, DataTable, EmptyState, Textarea } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

const TABS = [
  { key: "explorer",  label: "Explorer",         icon: Search },
  { key: "timeline",  label: "Timeline",         icon: Clock },
  { key: "decisions", label: "Decisions",        icon: Landmark },
  { key: "lessons",   label: "Lessons learned",  icon: BookOpen },
  { key: "evidence",  label: "Evidence vault",   icon: Archive },
];

export default function Knowledge() {
  const { has } = usePermissions();
  const canWrite = has("data:knowledge:write");
  const [tab, setTab] = useState("explorer");
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/knowledge/stats").then(r => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-6" data-testid="knowledge-page">
      <PageHero
        eyebrow="Knowledge360™"
        title="Institutional memory of Thom Homestead"
        subtitle="Every decision, lesson, insight and event — permanently recorded, searchable and linked. Ask questions in plain English; get answers grounded in the actual records."
        moduleKind="dashboard"
      />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard testid="know-stat-decisions" label="Decisions" value={stats?.decisions ?? 0} icon={Landmark} tone="primary" />
        <StatCard testid="know-stat-lessons" label="Lessons" value={stats?.lessons ?? 0} icon={BookOpen} tone="info" />
        <StatCard testid="know-stat-timeline" label="Timeline events" value={stats?.timeline_events ?? 0} icon={Clock} tone="accent" />
        <StatCard testid="know-stat-evidence" label="Evidence items" value={stats?.evidence_items ?? 0} icon={Archive} tone="muted" />
        <StatCard testid="know-stat-summaries" label="Summaries" value={stats?.summaries ?? 0} icon={Sparkles} tone="success" />
        <StatCard testid="know-stat-insights" label="Active insights" value={stats?.active_insights ?? 0} icon={Sparkles} tone="primary" />
      </div>

      <Section className="relative overflow-hidden">
        <Pattern kind="dashboard" />
        <div className="relative">
          <div className="flex flex-wrap gap-1.5 mb-5 p-1 rounded-full border border-border bg-muted/40 w-fit">
            {TABS.map(t => (
              <button
                key={t.key}
                data-testid={`know-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${tab === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "explorer" && <Explorer />}
          {tab === "timeline" && <Timeline />}
          {tab === "decisions" && <Decisions canWrite={canWrite} />}
          {tab === "lessons" && <Lessons canWrite={canWrite} />}
          {tab === "evidence" && <Evidence canWrite={canWrite} />}
        </div>
      </Section>
    </div>
  );
}

function Explorer() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [ans, setAns] = useState(null);
  const [corpus, setCorpus] = useState([]);

  async function ask() {
    if (!q.trim()) return;
    setBusy(true); setAns(null);
    try {
      const { data } = await api.post("/knowledge/ask", { question: q });
      setAns(data.summary?.answer || "");
      setCorpus(data.corpus || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="e.g. Show every poultry mortality event this year, or When was the borehole repaired?"
          onKeyDown={e => e.key === "Enter" && ask()}
          data-testid="know-ask-input"
        />
        <Button variant="primary" onClick={ask} disabled={busy} data-testid="know-ask-btn"><Send className="w-4 h-4" /> {busy ? "Thinking…" : "Ask"}</Button>
      </div>

      {ans && (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="know-answer">
          <div className="eyebrow mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-primary" /> Home360 Executive Knowledge Assistant</div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">{ans}</pre>
        </div>
      )}

      {corpus.length > 0 && (
        <div>
          <div className="eyebrow mb-2">Records referenced ({corpus.length})</div>
          <DataTable
            testid="know-corpus"
            columns={[
              { header: "Source", cell: r => <Badge variant="primary">{r.collection}</Badge> },
              { header: "Title", cell: r => <span className="font-medium">{r.title}</span> },
              { header: "Snippet", key: "snippet" },
              { header: "When", cell: r => r.occurred_at?.slice(0, 16).replace("T", " ") },
            ]}
            rows={corpus}
          />
        </div>
      )}
    </div>
  );
}

function Timeline() {
  const [rows, setRows] = useState([]);
  const [module, setModule] = useState("");
  const [kind, setKind] = useState("");
  useEffect(() => {
    const qs = new URLSearchParams();
    if (module) qs.set("module", module);
    if (kind) qs.set("kind", kind);
    api.get(`/knowledge/timeline?${qs.toString()}`).then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, [module, kind]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={module} onChange={e => setModule(e.target.value)} className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs">
          <option value="">All modules</option>
          {["executive", "poultry", "health", "finance", "construction", "inventory", "hr", "marketing", "briefs", "system"].map(m => <option key={m}>{m}</option>)}
        </select>
        <select value={kind} onChange={e => setKind(e.target.value)} className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs">
          <option value="">All kinds</option>
          {["decision", "lesson", "intel_insight", "brief", "activity"].map(k => <option key={k}>{k}</option>)}
        </select>
      </div>
      {rows.length === 0 ? <EmptyState icon={Clock} title="No timeline events yet" description="Every decision, lesson, insight and executive action will appear here as it happens." /> :
        <ol className="relative border-l border-border ml-3" data-testid="know-timeline">
          {rows.map(r => (
            <li key={r.id} className="ml-6 mb-4 relative">
              <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
              <div className="rounded-xl border border-border p-3 bg-card">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Badge variant="primary">{r.kind}</Badge>
                  <Badge variant="neutral">{r.module}</Badge>
                  <span className="text-[11px] text-muted-foreground">{r.occurred_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-sm font-medium">{r.title}</div>
                {r.summary && <div className="text-xs text-muted-foreground mt-1">{r.summary}</div>}
              </div>
            </li>
          ))}
        </ol>}
    </div>
  );
}

function Decisions({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ status: "open", module: "executive" });
  const [showForm, setShowForm] = useState(false);
  async function load() { const r = await api.get("/knowledge/decisions"); setRows(r.data || []); }
  useEffect(() => { load(); }, []);
  async function submit(e) {
    e.preventDefault();
    try { await api.post("/knowledge/decisions", form); toast.success("Decision recorded"); setForm({ status: "open", module: "executive" }); setShowForm(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div>
      {canWrite && (
        <Button variant="primary" onClick={() => setShowForm(v => !v)} className="mb-4" data-testid="know-decision-add-btn"><Plus className="w-4 h-4" /> Record decision</Button>
      )}
      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-border bg-muted/30 p-5 mb-5 grid md:grid-cols-3 gap-3">
          <Field label="Title"><Input value={form.title || ""} onChange={e => upd("title", e.target.value)} required data-testid="decision-form-title" /></Field>
          <Field label="Module">
            <select value={form.module} onChange={e => upd("module", e.target.value)} className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm">
              {["executive", "poultry", "health", "construction", "finance", "inventory", "hr", "marketing"].map(v => <option key={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Decision date"><Input type="date" value={form.decision_date || ""} onChange={e => upd("decision_date", e.target.value)} required /></Field>
          <Field label="Decision" className="md:col-span-3"><Textarea value={form.decision || ""} onChange={e => upd("decision", e.target.value)} required /></Field>
          <Field label="Reason" className="md:col-span-3"><Textarea value={form.reason || ""} onChange={e => upd("reason", e.target.value)} /></Field>
          <Field label="Expected benefit"><Input value={form.expected_benefit || ""} onChange={e => upd("expected_benefit", e.target.value)} /></Field>
          <Field label="Responsible person"><Input value={form.responsible_person || ""} onChange={e => upd("responsible_person", e.target.value)} /></Field>
          <Field label="Target completion"><Input type="date" value={form.target_completion || ""} onChange={e => upd("target_completion", e.target.value)} /></Field>
          <div className="md:col-span-3 flex justify-end gap-2"><Button variant="ghost" type="button" onClick={() => setShowForm(false)}>Cancel</Button><Button variant="primary" type="submit" data-testid="decision-form-submit">Save decision</Button></div>
        </form>
      )}
      {rows.length === 0 ? <EmptyState icon={Landmark} title="No decisions logged" description="Executive decisions recorded here become part of the permanent institutional record." /> :
        <DataTable
          testid="decisions-table"
          columns={[
            { header: "Date", key: "decision_date" },
            { header: "Title", cell: r => <span className="font-medium">{r.title}</span> },
            { header: "Module", cell: r => <Badge variant="primary">{r.module}</Badge> },
            { header: "Responsible", key: "responsible_person" },
            { header: "Target", key: "target_completion" },
            { header: "Status", cell: r => <Badge variant={r.status === "completed" ? "healthy" : r.status === "abandoned" ? "critical" : "pending"}>{r.status}</Badge> },
          ]}
          rows={rows}
        />
      }
    </div>
  );
}

function Lessons({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ module: "executive" });
  const [showForm, setShowForm] = useState(false);
  async function load() { const r = await api.get("/knowledge/lessons"); setRows(r.data || []); }
  useEffect(() => { load(); }, []);
  async function submit(e) {
    e.preventDefault();
    try { await api.post("/knowledge/lessons", form); toast.success("Lesson recorded"); setForm({ module: "executive" }); setShowForm(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div>
      {canWrite && <Button variant="primary" onClick={() => setShowForm(v => !v)} className="mb-4"><Plus className="w-4 h-4" /> Record lesson</Button>}
      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-border bg-muted/30 p-5 mb-5 grid md:grid-cols-2 gap-3">
          <Field label="Title"><Input value={form.title || ""} onChange={e => upd("title", e.target.value)} required /></Field>
          <Field label="Module">
            <select value={form.module} onChange={e => upd("module", e.target.value)} className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm">
              {["executive", "poultry", "health", "construction", "finance", "inventory", "hr", "marketing"].map(v => <option key={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Event date"><Input type="date" value={form.event_date || ""} onChange={e => upd("event_date", e.target.value)} required /></Field>
          <Field label="Next review"><Input type="date" value={form.next_review_date || ""} onChange={e => upd("next_review_date", e.target.value)} /></Field>
          <Field label="What happened" className="md:col-span-2"><Textarea value={form.what_happened || ""} onChange={e => upd("what_happened", e.target.value)} required /></Field>
          <Field label="Why it happened" className="md:col-span-2"><Textarea value={form.why_it_happened || ""} onChange={e => upd("why_it_happened", e.target.value)} /></Field>
          <Field label="What worked"><Textarea value={form.what_worked || ""} onChange={e => upd("what_worked", e.target.value)} /></Field>
          <Field label="What failed"><Textarea value={form.what_failed || ""} onChange={e => upd("what_failed", e.target.value)} /></Field>
          <Field label="Recommendations" className="md:col-span-2"><Textarea value={form.recommendations || ""} onChange={e => upd("recommendations", e.target.value)} /></Field>
          <div className="md:col-span-2 flex justify-end gap-2"><Button variant="ghost" type="button" onClick={() => setShowForm(false)}>Cancel</Button><Button variant="primary" type="submit">Save lesson</Button></div>
        </form>
      )}
      {rows.length === 0 ? <EmptyState icon={BookOpen} title="No lessons yet" description="Post-event learnings recorded here build the homestead's organisational experience." /> :
        <DataTable
          columns={[
            { header: "Event date", key: "event_date" },
            { header: "Title", cell: r => <span className="font-medium">{r.title}</span> },
            { header: "Module", cell: r => <Badge variant="primary">{r.module}</Badge> },
            { header: "Summary", cell: r => <span className="line-clamp-1">{r.what_happened}</span> },
            { header: "Next review", key: "next_review_date" },
          ]}
          rows={rows}
        />
      }
    </div>
  );
}

function Evidence({ canWrite }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/knowledge/evidence").then(r => setRows(r.data || [])).catch(() => setRows([])); }, []);
  return rows.length === 0 ?
    <EmptyState icon={Archive} title="Evidence vault empty" description="Attach photos, GPS-stamped receipts, signed approvals and QR-verifiable documents to preserve them permanently." /> :
    <DataTable
      columns={[
        { header: "When", cell: r => r.occurred_at?.slice(0, 16).replace("T", " ") },
        { header: "Title", cell: r => <span className="font-medium">{r.title}</span> },
        { header: "Module", cell: r => <Badge variant="primary">{r.module}</Badge> },
        { header: "GPS", cell: r => r.gps_lat ? `${r.gps_lat.toFixed(4)}, ${r.gps_lng.toFixed(4)}` : "—" },
        { header: "Captured by", key: "captured_by" },
      ]}
      rows={rows}
    />;
}
